import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Tejedora (docs/CRIATURAS.md §2, era III): the saboteur of the third era, a spider of the dream as big as a man,
 * standing on eight long jointed legs that reach twice that across. Its knees ride high over the body, the joints
 * between the segments are pale and half seen through, and the legs end in needle points. The bloated abdomen is
 * ink-black velvet carrying a pale mask on its back, a woman's face with hollow eyes, framed by threads that run
 * out loose toward the spinnerets like a tapestry left unfinished; underneath, an ember hourglass. A cluster of
 * eight small eyes burns a pale poison green over the chelicerae, whose fangs drip venom of the same light. From
 * the spinnerets trails the silk it cocoons the towers with (the `silk` bone, which the clips draw out).
 *
 * Opaque shared materials; the venom (eyes and drops) is its one self-lit material and the silk (also the joint
 * membranes) its one translucent one, plus glow sprites and the ink every nightmare sheds.
 */

const UP = V(0, 1, 0);
/** Uniform size of the miniature against its modelled coordinates. */
export const SIZE = 0.7;
/** The venom's light. */
export const VENOM = '#a6ff5c';

/** A bone turned about Y so its local +Z runs out along the leg (its X turns lift and lower the leg). */
export const yjoint = (parentAbs: THREE.Object3D, at: THREE.Vector3, yaw: number, name: string): { p: THREE.Group; abs: THREE.Group } => {
  const p = new THREE.Group();
  p.name = name;
  p.position.copy(at);
  p.rotation.y = yaw;
  parentAbs.add(p);
  const abs = new THREE.Group();
  abs.rotation.y = -yaw;
  abs.position.copy(at).negate().applyAxisAngle(UP, -yaw);
  p.add(abs);
  return { p, abs };
};

/** A tapering tube along a curve (leg segments, fangs, palps), closed at a point at the tip. */
export const taper = (pts: THREE.Vector3[], r0: number, r1: number, seg = 6, rad = 6, flat = 1, tipK = 1.5): THREE.BufferGeometry => {
  const curve = new THREE.CatmullRomCurve3(pts);
  const fr = curve.computeFrenetFrames(seg, false);
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= seg; i++) {
    const k = i / seg;
    const c = curve.getPointAt(k);
    const r = r0 + (r1 - r0) * k;
    const n = fr.normals[i] as THREE.Vector3, b = fr.binormals[i] as THREE.Vector3;
    for (let j = 0; j < rad; j++) {
      const a = (j / rad) * Math.PI * 2;
      const p = c.clone().addScaledVector(n, Math.cos(a) * r * flat).addScaledVector(b, Math.sin(a) * r);
      pos.push(p.x, p.y, p.z);
    }
  }
  const tip = curve.getPointAt(1).addScaledVector(fr.tangents[seg] as THREE.Vector3, r1 * tipK);
  pos.push(tip.x, tip.y, tip.z);
  const ti = (seg + 1) * rad;
  for (let i = 0; i < seg; i++) {
    for (let j = 0; j < rad; j++) {
      const a = i * rad + j, b2 = i * rad + ((j + 1) % rad), c = a + rad, d = b2 + rad;
      idx.push(a, c, b2, b2, c, d);
    }
  }
  for (let j = 0; j < rad; j++) idx.push(seg * rad + j, ti, seg * rad + ((j + 1) % rad));
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/**
 * A closed skin over a surface `P(u, v)` (u wraps round, v runs pole to pole) on an nu × nv grid, each cell going
 * to the material `paint(i, j)` picks, with smooth normals across the cells so a pattern reads as painted on.
 * `axis(v)` is a point inside the body at that v (to turn the normals outward).
 */
export const skin = (parent: THREE.Object3D, P: (u: number, v: number) => THREE.Vector3, axis: (v: number) => THREE.Vector3, nu: number, nv: number, paint: (i: number, j: number) => THREE.Material): void => {
  const pt: THREE.Vector3[][] = [];
  const nm: THREE.Vector3[][] = [];
  for (let i = 0; i <= nu; i++) {
    pt.push([]);
    for (let j = 0; j <= nv; j++) (pt[i] as THREE.Vector3[]).push(P(i / nu, j / nv));
  }
  const at = (i: number, j: number): THREE.Vector3 => (pt[((i % nu) + nu) % nu] as THREE.Vector3[])[Math.max(0, Math.min(nv, j))] as THREE.Vector3;
  for (let i = 0; i <= nu; i++) {
    nm.push([]);
    for (let j = 0; j <= nv; j++) {
      const p = at(i, j);
      const out = p.clone().sub(axis(j / nv));
      let n = at(i + 1, j).clone().sub(at(i - 1, j)).cross(at(i, j + 1).clone().sub(at(i, j - 1)));
      if (n.lengthSq() < 1e-10 || j === 0 || j === nv) n = j === 0 ? at(0, 1).clone().sub(at(0, 2)).setX(0) : j === nv ? at(0, nv - 1).clone().sub(at(0, nv - 2)).setX(0) : out.clone();
      n.normalize();
      if (j === 0 || j === nv) {
        // At the poles, the mean of the ring next to them, pushed out along the axis.
        const ring = V(0, 0, 0);
        const jj = j === 0 ? 1 : nv - 1;
        for (let k = 0; k < nu; k++) ring.add(at(k, jj));
        ring.multiplyScalar(1 / nu);
        n = p.clone().sub(ring).normalize();
      } else if (n.dot(out) < 0) n.negate();
      (nm[i] as THREE.Vector3[]).push(n);
    }
  }
  const O = (i: number, j: number): THREE.Vector3 => (pt[i] as THREE.Vector3[])[j] as THREE.Vector3;
  const N = (i: number, j: number): THREE.Vector3 => (nm[i] as THREE.Vector3[])[j] as THREE.Vector3;
  const buckets = new Map<THREE.Material, { p: number[]; n: number[] }>();
  const put = (m: THREE.Material, q: [number, number][], want: THREE.Vector3): void => {
    const b = buckets.get(m) ?? { p: [], n: [] };
    buckets.set(m, b);
    const vs = q.map(([a, c]) => O(a, c)), ns = q.map(([a, c]) => N(a, c));
    const f = (vs[1] as THREE.Vector3).clone().sub(vs[0] as THREE.Vector3).cross((vs[2] as THREE.Vector3).clone().sub(vs[0] as THREE.Vector3));
    if (f.lengthSq() < 1e-12) return;
    const order = f.dot(want) >= 0 ? [0, 1, 2] : [0, 2, 1];
    for (const k of order) {
      const v = vs[k] as THREE.Vector3, n = ns[k] as THREE.Vector3;
      b.p.push(v.x, v.y, v.z);
      b.n.push(n.x, n.y, n.z);
    }
  };
  for (let i = 0; i < nu; i++) {
    for (let j = 0; j < nv; j++) {
      const m = paint(i, j);
      const want = N(i, j).clone().add(N(i + 1, j + 1)).add(N(i + 1, j)).add(N(i, j + 1));
      put(m, [[i, j], [i + 1, j], [i + 1, j + 1]], want);
      put(m, [[i, j], [i + 1, j + 1], [i, j + 1]], want);
    }
  }
  for (const [m, b] of buckets) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(b.p, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(b.n, 3));
    M(g, m, parent);
  }
};

/** Each leg's hip (right side; the left mirrors x), its yaw off the nose, and its knee, ankle and toe as (reach from the hip, height). */
const LEG: readonly { hip: THREE.Vector3; yaw: number; knee: [number, number]; ankle: [number, number]; toe: [number, number] }[] = [
  { hip: V(0.17, 0.56, 0.42), yaw: 0.42, knee: [0.66, 1.22], ankle: [1.36, 0.66], toe: [1.7, 0.0] },
  { hip: V(0.23, 0.55, 0.3), yaw: 1.08, knee: [0.58, 1.14], ankle: [1.18, 0.56], toe: [1.42, 0.0] },
  { hip: V(0.24, 0.55, 0.14), yaw: 1.88, knee: [0.54, 1.1], ankle: [1.1, 0.52], toe: [1.34, 0.0] },
  { hip: V(0.19, 0.55, 0.0), yaw: 2.5, knee: [0.64, 1.2], ankle: [1.32, 0.6], toe: [1.66, 0.0] },
];
/** Leg order (bones leg0…leg7): right front to back, then left front to back. */
export const legOf = (i: number): { side: number; row: number } => ({ side: i < 4 ? 1 : -1, row: i % 4 });

/**
 * Bones: `body` (the cephalothorax, pivoting over its middle) carries everything; the `abdomen` hangs from the
 * waist (it breathes and swings), the `silk` sits at the spinnerets (scale draws the thread out), the chelicerae
 * `chelL`/`chelR` (−X swings the fangs up and forward), the `venom` between the fangs (its glow and drool; scale
 * swells it), the palps `palpL`/`palpR`, and eight legs, each a `leg<i>` at the hip, a `knee<i>` and a `foot<i>`
 * at the ankle, turned so local +Z runs out along the leg: −X lifts, a Y turn swings it fore and aft, +X on the
 * knee and foot folds them in. Legs 0–3 are the right side front to back, 4–7 the left.
 */
export const weaver = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.9, 61);
  const { g, R, rng } = c;

  // Polished ink on the carapace and legs, a soft velvet black on the abdomen, a cold bone for the mask.
  const ink = std('#0e0c13', { roughness: 0.26, metalness: 0.45, bumpScale: 0.006 });
  const sheen = std('#1d1830', { roughness: 0.22, metalness: 0.5, bumpScale: 0.006 });
  const velvet = std('#0a090d', { roughness: 0.72, metalness: 0.1, bumpScale: 0.03 });
  const ash = std('#4c4954', { roughness: 0.8, bumpScale: 0.02 });
  const bone = std('#c3c8ce', { roughness: 0.62, bumpScale: 0.015 });
  const boneD = std('#80868e', { roughness: 0.7, bumpScale: 0.02 });
  const ember = std('#9c2a1f', { roughness: 0.5, bumpScale: 0.01 });
  const fangM = std('#c4c6c4', { roughness: 0.35, metalness: 0.1 });
  const venom = emis('#d8ffb0', '#8cff3a', 1.7);
  // The silk, and the membranes at the joints: pale, half seen through.
  const silk = std('#b4bac8', { roughness: 0.45, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide, bumpScale: 0 });

  const sc = G(g);
  sc.scale.setScalar(SIZE);
  const bodyJ = joint(sc, V(0, 0.6, 0.2), 'body');
  const bd = bodyJ.abs;
  const abdJ = joint(bd, V(0, 0.66, -0.08), 'abdomen');
  const ab = abdJ.abs;
  const chelJ = [-1, 1].map((k) => joint(bd, V(k * 0.065, 0.63, 0.64), k < 0 ? 'chelL' : 'chelR'));
  const venomJ = joint(bd, V(0, 0.42, 0.74), 'venom');
  const palpJ = [-1, 1].map((k) => joint(bd, V(k * 0.13, 0.56, 0.6), k < 0 ? 'palpL' : 'palpR'));

  // ---------- Abdomen ----------
  // Pear-shaped and swollen toward the rear, which rides a little high; u round from the top (finer over the back,
  // where the mask is), v from the waist to the spinnerets.
  const Z0 = -0.1, Z1 = -1.42;
  const cy = (v: number): number => 0.8 + 0.14 * v;
  const rad = (v: number): number => 0.58 * Math.sin(Math.PI * Math.min(1, v ** 1.35)) ** 0.62 * (1 + 0.04 * Math.sin(v * Math.PI * 3));
  const abdAt = (a: number, v: number): THREE.Vector3 => {
    const r = rad(v);
    return V(r * Math.sin(a), cy(v) + 0.84 * r * Math.cos(a) - 0.05 * r * Math.max(0, -Math.cos(a)), Z0 + (Z1 - Z0) * v);
  };
  const axisAt = (v: number): THREE.Vector3 => V(0, cy(v), Z0 + (Z1 - Z0) * v);
  skin(ab, (u, v) => abdAt((u * 2 - 1) * Math.PI, v), axisAt, 12, 12, () => velvet);
  /** The surface normal of the abdomen at (a, v). */
  const abdN = (a: number, v: number): THREE.Vector3 => {
    const e = 0.004;
    const n = abdAt(a + e, v).sub(abdAt(a - e, v)).cross(abdAt(a, Math.min(0.999, v + e)).sub(abdAt(a, Math.max(0.001, v - e)))).normalize();
    return n.dot(abdAt(a, v).sub(axisAt(v))) < 0 ? n.negate() : n;
  };
  /**
   * Paint laid on the abdomen: a ns × nt patch whose (s, t) map to a place (a, v) on it, lifted `lift` off the
   * skin (layers stack by lift).
   */
  const decal = (F: (s: number, t: number) => [number, number], ns: number, nt: number, mat: THREE.Material, lift: number): void => {
    const pos: number[] = [], nor: number[] = [], idx: number[] = [];
    for (let i = 0; i <= ns; i++) {
      for (let j = 0; j <= nt; j++) {
        const [a, v] = F(i / ns, j / nt);
        const n = abdN(a, v);
        const p = abdAt(a, v).addScaledVector(n, lift);
        pos.push(p.x, p.y, p.z);
        nor.push(n.x, n.y, n.z);
      }
    }
    for (let i = 0; i < ns; i++) {
      for (let j = 0; j < nt; j++) {
        const q = i * (nt + 1) + j;
        idx.push(q, q + 1, q + nt + 1, q + 1, q + nt + 2, q + nt + 1);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
    // Wind every triangle to face out.
    for (let k = 0; k < idx.length; k += 3) {
      const [i0, i1, i2] = [idx[k]!, idx[k + 1]!, idx[k + 2]!];
      const P = (i: number): THREE.Vector3 => V(pos[i * 3]!, pos[i * 3 + 1]!, pos[i * 3 + 2]!);
      const f = P(i1).sub(P(i0)).cross(P(i2).sub(P(i0)));
      if (f.dot(V(nor[i0 * 3]!, nor[i0 * 3 + 1]!, nor[i0 * 3 + 2]!)) < 0) {
        idx[k + 1] = i2;
        idx[k + 2] = i1;
      }
    }
    geo.setIndex(idx);
    M(geo, mat, ab);
  };
  /** A thread of paint along the abdomen from (a0, v0) to (a1, v1), `w` radians wide. */
  const thread = (a0: number, v0: number, a1: number, v1: number, w: number, mat: THREE.Material, lift = 0.006, n = 5): void =>
    decal((s, t) => [a0 + (a1 - a0) * t + (s - 0.5) * w * (1 - 0.3 * t), v0 + (v1 - v0) * t], 1, n, mat, lift);

  // The mask: a woman's face, chin toward the waist and brow toward the spinnerets (so it looks ahead when the
  // abdomen rears to spin), its edge shaded.
  const MV = 0.5;
  const faceAt = (rho: number, th: number): [number, number] => {
    const chin = 1 - 0.32 * Math.max(0, Math.cos(th)) ** 2;
    return [rho * 0.7 * Math.sin(th) * chin, MV - rho * 0.3 * Math.cos(th) * (Math.cos(th) > 0 ? 1.12 : 1)];
  };
  decal((s, t) => faceAt(1.0 + 0.1 * s, t * Math.PI * 2), 1, 18, boneD, 0.005);
  decal((s, t) => faceAt(s, t * Math.PI * 2), 3, 18, bone, 0.007);
  // Hollow eyes under a heavy brow, each weeping a line of ink to the jaw; a closed mouth.
  for (const k of [-1, 1]) {
    decal((s, t) => {
      const th = t * Math.PI * 2;
      // An almond, its outer corner drooping toward the jaw.
      const x = Math.sin(th);
      return [k * (0.25 + s * 0.15 * x), MV + 0.08 - s * (0.042 * Math.cos(th) + 0.03 * x)];
    }, 2, 12, velvet, 0.011);
    // A line of ink weeping from each to the jaw.
    decal((q, t) => [k * (0.28 + 0.015 * Math.sin(t * 5) + (q - 0.5) * 0.026 * (1 - 0.7 * t)), MV + 0.05 - t * 0.3], 1, 5, velvet, 0.011);
  }
  // Lips pressed thin, the corners turned down; the ridge of the nose.
  decal((q, t) => [(t - 0.5) * 0.24, MV - 0.19 + (q - 0.5) * 0.014 * (1 - Math.abs(t - 0.5)) - 0.04 * (t - 0.5) ** 2], 1, 6, velvet, 0.011);
  thread(0, MV + 0.06, 0, MV - 0.1, 0.035, boneD, 0.01, 2);
  // The tapestry round it: warp threads running back from a woven band over the waist, framing the face and
  // left unfinished at uneven lengths, their last stretch gone to ash; a few weft threads broken off across them.
  thread(-1.55, 0.16, 1.55, 0.16, 0.06, bone, 0.006, 10);
  for (let w = 0; w < 4; w++) {
    for (const k of [-1, 1]) {
      const a = k * (0.92 + w * 0.17);
      const end = 0.58 + (R() + 0.5) * 0.3 - w * 0.04;
      thread(a, 0.17, a + k * 0.03, end, 0.06, bone, 0.006, 4);
      thread(a + k * 0.03, end, a + k * (0.03 + R() * 0.1), end + 0.07 + (R() + 0.5) * 0.06, 0.03, ash, 0.006, 2);
    }
  }
  for (const [v, a0, a1] of [[0.42, 0.85, 1.5], [0.5, -1.45, -0.85], [0.62, 0.86, 1.2]] as const) thread(a0, v, a1, v + 0.015, 0.045, boneD, 0.008, 4);
  // Underneath, an ember hourglass.
  decal((s, t) => [Math.PI + (s - 0.5) * (0.06 + 1.1 * Math.abs(t - 0.5) ** 0.8), 0.46 + 0.22 * t], 1, 6, ember, 0.006);
  // The waist (pedicel), a short neck of ash.
  const ped = M(new THREE.CylinderGeometry(0.07, 0.09, 0.16, 6), ash, ab);
  ped.position.set(0, 0.68, -0.08);
  ped.rotation.x = Math.PI / 2 + 0.25;
  // Spinnerets: three ash fingers under the rear tip; the silk leaves from their mouths.
  const spin = abdAt(Math.PI, 0.93);
  for (const [x, l, tilt] of [[-0.05, 0.09, -0.4], [0.05, 0.09, 0.4], [0, 0.11, 0]] as const) {
    const s = M(new THREE.ConeGeometry(0.035, l, 5), ash, ab);
    s.position.set(spin.x + x, spin.y - 0.01, spin.z - 0.02);
    s.rotation.set(-2.3, 0, tilt);
  }
  const tip = V(0, spin.y - 0.06, spin.z - 0.08);
  const silkJ = joint(ab, tip, 'silk');
  // The silk: a sheaf of fine threads drawn out of the spinnerets, wavering (and, left behind, a drag line sagging
  // to the ground).
  const sl = silkJ.abs;
  for (let s = 0; s < 6; s++) {
    const a = (s - 2.5) * 0.06;
    const len = 0.24 + 0.05 * Math.cos(s * 1.7);
    const dir = V(Math.sin(a), -0.22 - 0.04 * Math.abs(s - 2.5), -Math.cos(a));
    const pts = [0, 0.33, 0.66, 1].map((k) => tip.clone().addScaledVector(dir, len * k).add(V(0.012 * Math.sin(k * 7 + s * 2), -0.02 * Math.sin(k * Math.PI), 0)));
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 5, 0.0032, 3), silk, sl);
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([tip, tip.clone().add(V(0.02, -0.2, -0.12)), tip.clone().add(V(0.03, -tip.y + 0.01, -0.34))]), 5, 0.0032, 3), silk, ab);
  glowSprite(sl, '#dfe6ff', 0.22, tip.clone().add(V(0, -0.04, -0.12)), 0.18);

  // ---------- Cephalothorax ----------
  const thx = M(new THREE.SphereGeometry(1, 10, 7), ink, bd);
  thx.position.set(0, 0.6, 0.22);
  thx.scale.set(0.3, 0.17, 0.37);
  const head = M(new THREE.SphereGeometry(1, 8, 5), ink, bd);
  head.position.set(0, 0.66, 0.5);
  head.scale.set(0.19, 0.15, 0.17);
  // The groove from the head back to the fovea.
  const fov = M(new THREE.SphereGeometry(1, 6, 3), sheen, bd);
  fov.position.set(0, 0.765, 0.16);
  fov.scale.set(0.05, 0.012, 0.16);
  // Sternum.
  const ster = M(new THREE.SphereGeometry(1, 8, 4), velvet, bd);
  ster.position.set(0, 0.48, 0.22);
  ster.scale.set(0.2, 0.07, 0.26);

  // Eyes: eight, in two rows over the front of the head, the middle pair biggest.
  const EYES: [number, number, number, number][] = [
    [0.045, 0.765, 0.645, 0.036], [0.105, 0.745, 0.625, 0.026], [0.055, 0.8, 0.6, 0.028], [0.13, 0.775, 0.575, 0.022],
  ];
  for (const [x, y, z, r] of EYES) {
    for (const k of [-1, 1]) {
      const e = M(new THREE.SphereGeometry(r, 4, 3), venom, bd);
      e.position.set(k * x, y, z);
    }
  }
  glowSprite(bd, VENOM, 0.42, V(0, 0.78, 0.63), 0.42);
  for (const k of [-1, 1]) glowSprite(bd, VENOM, 0.13, V(k * 0.045, 0.77, 0.67), 0.9);

  // ---------- Chelicerae and fangs, dripping ----------
  for (const k of [-1, 1]) {
    const cj = chelJ[k < 0 ? 0 : 1]!.abs;
    M(taper([V(k * 0.065, 0.63, 0.62), V(k * 0.08, 0.55, 0.7), V(k * 0.07, 0.45, 0.74)], 0.075, 0.045, 4, 6, 1, 0.6), ink, cj);
    // The fang: folded in under the chelicera, a curved needle.
    M(taper([V(k * 0.07, 0.44, 0.75), V(k * 0.055, 0.37, 0.77), V(k * 0.02, 0.34, 0.73)], 0.022, 0.006, 4, 4), fangM, cj);
    const drop = M(new THREE.SphereGeometry(0.016, 5, 4), venom, cj);
    drop.position.set(k * 0.018, 0.325, 0.73);
    drop.scale.set(1, 1.5, 1);
    glowSprite(cj, VENOM, 0.1, V(k * 0.02, 0.33, 0.73), 0.7);
  }
  // The venom between them: a string of it hanging from the mouth, and its glow.
  const vn = venomJ.abs;
  M(taper([V(0, 0.44, 0.74), V(0.005, 0.36, 0.745), V(-0.005, 0.27, 0.74)], 0.012, 0.004, 4, 4), venom, vn);
  const bead = M(new THREE.SphereGeometry(0.018, 5, 4), venom, vn);
  bead.position.set(-0.005, 0.26, 0.74);
  bead.scale.set(1, 1.4, 1);
  glowSprite(vn, VENOM, 0.36, V(0, 0.37, 0.75), 0.45);

  // Palps: short feelers before the fangs, clubbed at the end.
  for (const k of [-1, 1]) {
    const pj = palpJ[k < 0 ? 0 : 1]!.abs;
    M(taper([V(k * 0.13, 0.56, 0.6), V(k * 0.2, 0.62, 0.74), V(k * 0.2, 0.46, 0.86), V(k * 0.17, 0.3, 0.9)], 0.03, 0.022, 5, 4), ink, pj);
    const club = M(new THREE.SphereGeometry(0.032, 4, 3), sheen, pj);
    club.position.set(k * 0.17, 0.29, 0.9);
    club.scale.set(1, 1.3, 1);
  }

  // ---------- Legs ----------
  const legBones: Record<string, THREE.Object3D> = {};
  for (let i = 0; i < 8; i++) {
    const { side, row } = legOf(i);
    const L = LEG[row]!;
    const hip = V(side * L.hip.x, L.hip.y, L.hip.z);
    const yaw = side * L.yaw;
    const d = V(Math.sin(yaw), 0, Math.cos(yaw));
    const P = ([r, y]: [number, number]): THREE.Vector3 => hip.clone().addScaledVector(d, r).setY(y);
    const knee = P(L.knee), ankle = P(L.ankle), toe = P(L.toe);
    const th = yjoint(bd, hip, yaw, `leg${i}`);
    const sh = yjoint(th.abs, knee, yaw, `knee${i}`);
    const ft = yjoint(sh.abs, ankle, yaw, `foot${i}`);
    legBones[`leg${i}`] = th.p;
    legBones[`knee${i}`] = sh.p;
    legBones[`foot${i}`] = ft.p;
    // Femur: rising steeply to the knee, bowed up, a little flattened.
    const fm = hip.clone().lerp(knee, 0.5).add(V(0, 0.04, 0)).addScaledVector(d, -0.04);
    M(taper([hip, fm, knee], 0.05, 0.038, 4, 4, 0.8, 0.2), ink, th.abs);
    // The knee: a pale membrane between the segments.
    const kn = M(new THREE.SphereGeometry(0.036, 5, 3), silk, sh.abs);
    kn.position.copy(knee);
    kn.scale.set(1, 1.1, 1.3);
    // Tibia: out and down to the ankle, banded pale near its end.
    const tm = knee.clone().lerp(ankle, 0.5).addScaledVector(d, 0.03).add(V(0, 0.03, 0));
    M(taper([knee.clone().addScaledVector(d, 0.03).add(V(0, -0.02, 0)), tm, ankle], 0.036, 0.026, 3, 4, 1, 0.2), ink, sh.abs);
    const band = ankle.clone().lerp(knee, 0.16);
    const ring = M(new THREE.CylinderGeometry(0.03, 0.032, 0.05, 6, 1, true), boneD, sh.abs);
    ring.position.copy(band);
    ring.quaternion.setFromUnitVectors(UP, knee.clone().sub(ankle).normalize());
    // The ankle's membrane, then the metatarsus and tarsus: a long needle down to the ground.
    const an = M(new THREE.SphereGeometry(0.026, 5, 3), silk, ft.abs);
    an.position.copy(ankle);
    const am = ankle.clone().lerp(toe, 0.5).addScaledVector(d, 0.03);
    M(taper([ankle, am, toe.clone().add(V(0, 0.015, 0))], 0.024, 0.008, 3, 4, 1, 2.2), ink, ft.abs);
  }

  // Ink seeping off the mask's eyes, like every nightmare.
  const ribs: Ribbon[] = [];
  const wm = basic('#2b2244', 0.42);
  for (const [x, z] of [[-0.18, -0.6], [0.2, -0.66]] as const) ribs.push(new Ribbon(ab, wisp(x, z, 0.5, 4).map((p) => p.add(V(0, 1.18, 0))), 0.022, wm, rng, 0.05));
  glowSprite(ab, '#5a3a8a', 1.4, V(0, 1.0, -0.7), 0.08);
  const throat = glowSprite(vn, VENOM, 0.5, V(0, 0.4, 0.76), 0.25, true);

  const t = (n: number): void => {
    for (const r of ribs) r.step();
    throat.material.opacity = 0.16 + ((n * 5) % 7) / 40;
  };
  t(0);
  return done(c, 1.32 * SIZE, t, undefined, bonesOf(c, {
    body: bodyJ.p, abdomen: abdJ.p, silk: silkJ.p, chelL: chelJ[0]!.p, chelR: chelJ[1]!.p, venom: venomJ.p, palpL: palpJ[0]!.p, palpR: palpJ[1]!.p,
    ...legBones,
  }));
};
